import { toaster } from '@optiaxiom/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { extractApiErrorMessage } from '@frontend/api/http';
import { AuthProvider, AuthProviderCreate, AuthProviderUpdate } from '@frontend/types';

import {
  createAuthProvider,
  deleteAuthProvider,
  getAuthProviders,
  updateAuthProvider,
} from '../services';

export function useGetAuthProviders() {
  const { data, isPending, error } = useQuery<AuthProvider[]>({
    queryKey: getAuthProviders().key,
    queryFn: getAuthProviders().queryFn,
  });

  return { providers: data ?? [], isPending, error };
}

export function useCreateAuthProvider(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  const { mutate, isPending, error } = useMutation({
    mutationFn: (payload: AuthProviderCreate) => createAuthProvider(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getAuthProviders().key });
      toaster.create('Auth provider created successfully', { type: 'success' });
      onSuccess?.();
    },
    onError: error => {
      const errorMessage = extractApiErrorMessage(error);
      toaster.create(errorMessage, { type: 'danger' });
    },
  });

  return { createProvider: mutate, isCreating: isPending, error };
}

export function useUpdateAuthProvider(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  const { mutate, isPending, error } = useMutation({
    mutationFn: ({ providerId, payload }: { providerId: string; payload: AuthProviderUpdate }) =>
      updateAuthProvider(providerId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getAuthProviders().key });
      toaster.create('Auth provider updated successfully', { type: 'success' });
      onSuccess?.();
    },
    onError: error => {
      const errorMessage = extractApiErrorMessage(error);
      toaster.create(errorMessage, { type: 'danger' });
    },
  });

  return { updateProvider: mutate, isUpdating: isPending, error };
}

export function useDeleteAuthProvider() {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, string>({
    mutationFn: providerId => deleteAuthProvider(providerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getAuthProviders().key });
      toaster.create('Auth provider deleted successfully', { type: 'success' });
    },
    onError: () => {
      toaster.create('Failed to delete auth provider', { type: 'danger' });
    },
  });
}
